import {computed, type ComputedRef, type Ref, ref} from 'vue'
import {defineStore} from 'pinia';
import type { Feature, MetadataDictionary, MetadataObject, GeoJSONFeatureCollection } from '@/types';
import type {GeoJSONGeometryCollection} from "ol/format/GeoJSON";
import {
  filterMetadataExclusive,
  getFilterItems
} from "@/stores/filtering";

const geoJSONURL = import.meta.env.VITE_GEO_JSON_DATA_URL;
const metadataURL = import.meta.env.VITE_METADATA_DATA_URL;

let localGeoJSON: unknown | undefined;
let localMetadata: unknown | undefined;

if (import.meta.env.DEV) {
  localGeoJSON = await import('@data/geojson_nov_2025.json');
  // import testingGeoJSON from '@data/geojson_oct_2024.json'; // Ensure this path is correct

  // import localMetadata from '@data/metadata_oct_2024.json';
  localMetadata = await import('@data/metadata_nov_2025.json');
}

const metadataPropertyId = 'gendib_sample_id';

export const useMetadataStore = defineStore('metadataStore', () => {
  const metadataDictionary: Ref<metadataObject> = ref([])
  const geoJsonCollection: Ref<GeoJSONFeatureCollection> = ref({
    type: 'FeatureCollection',
    name: 'unknown',
    crs: {
      type: 'name',
      properties: {
        name: 'unknown-crs'
      }
    },
    features: []
  });



  const selectionId = ref('')
  const featureRangeSelection = ref(100);

  const filterPropertyKeys = ref(['species_group', 'species_scientific', 'marker_types','canton','data_set_published','swiss_data']);
  const filterMap = ref(new Map<string, FilterItem[]>())

  const activeFilters = ref(new Map<string, string[] | null>);

  const hasActiveFilters = computed(() => {
    for (const [key, value] of activeFilters.value.entries()) {
      if (value !== null) {
        return true;
      }
    }
    return false;
  });

  const exclusiveFiltering = ref(true);
  // Add GBIF toggle state
  const showGbifData = ref(false);

  const speciesGroupMap = computed(() => {
    const map = new Map();
    Object.values(metadataDictionary.value).forEach(metadata => {
      if (metadata.gendib_sample_id && metadata.species_group) {
        map.set(metadata.gendib_sample_id, metadata.species_group);
      }
    });
    return map;
  });

  const showSensitiveData = ref(false);
  const showSwissDataOnly = ref('yes');
  const showDataSetPublishedOnly = ref('no');

  const selectedGeoJSON = computed(() => {
    return geoJsonCollection.value.features?.find((feature: Feature) => feature.properties[metadataPropertyId] === selectionId.value);
  })

  const selectedMetadata = computed(() => {
    return metadataDictionary.value[selectionId.value] || null;
  })

  /**
   * Make sure this is only called when the route changes.
   * So the flow is: onClick -> get the Id -> push the id to the route
   * -> watch in app.vue reacts on the change -> calls the selectRecord
   * @param id
   */
  function selectRecord(id: string) {
    selectionId.value = id;
  }

  function initializeFilters() {
    filterPropertyKeys.value.forEach((key) => {
      if (key === 'swiss_data') {
        activeFilters.value.set(key, showSwissDataOnly.value === 'yes' ? ['yes'] : null);
      } else {
        activeFilters.value.set(key, null);
      }
    });
  }

  initializeFilters();


  function clearSelection() {
    selectionId.value = '';
  }


  // Function to add timeout to a fetch request
  const fetchWithTimeout = async (url:string, timeout = 4000) => {
    return Promise.race([
      fetch(url),
      new Promise((_, reject) => setTimeout(() => reject(new Error('Request timed out')), timeout))
    ]);
  };

// Fetch metadata with fallback to local JSON
  const fetchMetadata = async () => {
    try {
      const response = await fetchWithTimeout(metadataURL) as Response; // Assert that response is of type Response

      // Check if response is OK (status code 200-299)
      if (!response.ok) {
        throw new Error(`Server responded with status: ${response.status}`);
      }

      // Use type assertion to specify that the response JSON will be of type MetadataDictionary
      const data: MetadataDictionary = await response.json();
      metadataDictionary.value = data;

      const metadataValues: MetadataObject[] = Object.values(metadataDictionary.value);
      updateFilterMap(filterPropertyKeys.value, metadataValues);
    } catch (e) {
      console.log(`Error fetching the url: ${metadataURL}. Using local fallback.`);

      // Fallback to local JSON data
      metadataDictionary.value = localMetadata as MetadataObject[]; // Use local JSON as fallback
      const metadataValues: MetadataObject[] = Object.values(metadataDictionary.value);
      updateFilterMap(filterPropertyKeys.value, metadataValues);
    }
  }

// Fetch GeoJSON with fallback to local JSON
  const fetchGeoJSON = async () => {
    try {
      const response = await fetchWithTimeout(geoJSONURL) as Response; // Assert that response is of type Response

      // Check if response is OK (status code 200-299)
      if (!response.ok) {
        throw new Error(`Server responded with status: ${response.status}`);
      }

      // Use type assertion to specify that the response JSON will be of type GeoJSONFeatureCollection
      const data: GeoJSONFeatureCollection = await response.json();
      geoJsonCollection.value = data;
    } catch (e) {
      console.log(`Error fetching the url: ${geoJSONURL}. Using local fallback.`);

      // Fallback to local JSON data
      geoJsonCollection.value = localGeoJSON as GeoJSONGeometryCollection; // Use local JSON as fallback
    }
  }

  const filteredFeatures = computed(() => {
    const filtered: GeoJSONGeometryCollection = geoJsonCollection.value;

    if (!filtered?.features) {
      return {
        type: 'FeatureCollection',
        features: [],
      };
    }

    let filteredGeoFeatures = filtered.features;

    if (filteredMetadata.value) {
      const metadataValues: MetadataObject[] = filteredMetadata.value;
      const filterIds = metadataValues.map((metadataObject) => metadataObject[metadataPropertyId]);

      const subset = [];
      for (let i = 0; i < filtered.features.length; i++) {
        const feat = filtered.features[i];
        if (filterIds.includes(feat.properties[metadataPropertyId])) {
          subset.push(feat);
        }
      }

      filteredGeoFeatures = subset;
    }

    return {
      type: 'FeatureCollection',
      features: filteredGeoFeatures,
    };
  })

  const setFeatureRangeSelection = (newRange: number) => {
    featureRangeSelection.value = newRange;
  }

  const updateFilterMap = (filterProperties: string[], metadataValues: MetadataObject[]) => {
    filterProperties.forEach((key) => {
      const filterItems = getFilterItems(metadataValues, key);
      filterMap.value.set(key, filterItems);
    });
/*
    console.log('updateFilterMap');
    console.log(filterMap.value.entries());
*/
  }

  const filteredMetadata: ComputedRef<MetadataObject[] | null> = computed(() => {
    const activeFiltersObject = Object.fromEntries(activeFilters.value);
    if (!hasActiveFilters.value) {
      return null;
    }
    const metadataValues = Object.values(metadataDictionary.value);
    return filterMetadataExclusive(metadataValues, activeFilters.value);
  });

  const changeFilter = (filterItem: { filterKey: string, propKey: string[] | null }) => {
    const { filterKey, propKey } : { filterKey: string, propKey: string[] | null } = filterItem;
    const filterPropertyValue = propKey && propKey.length > 0 ? propKey : null;
    activeFilters.value.set(filterKey, filterPropertyValue);

    if (exclusiveFiltering.value) {
      const metadataValues: MetadataObject[] = filteredMetadata.value ? filteredMetadata.value : Object.values(metadataDictionary.value);
      updateFilterMap(filterPropertyKeys.value, metadataValues);
    }
  }

  const changeExclusiveFiltering = (exclusive: boolean) => {
    exclusiveFiltering.value = exclusive;
    const metadataValues: MetadataObject[] = Object.values(metadataDictionary.value);
    updateFilterMap(filterPropertyKeys.value, metadataValues);
  }

  const isSensitive = (id: string) => {
    return metadataDictionary.value[id]?.sensitive_species;
  }

  const getSampleSize = (id: string) => {
    return metadataDictionary.value[id]?.sample_size || 1;
  }

  return {
    geoJsonCollection, metadataDictionary,
    filterMap, filterPropertyKeys,
    selectRecord, selectedGeoJSON, selectedMetadata,
    clearSelection,
    fetchMetadata, fetchGeoJSON,
    setFeatureRangeSelection,
    changeFilter, filteredFeatures, filteredMetadata,
    exclusiveFiltering, changeExclusiveFiltering,
    showSensitiveData, showSwissDataOnly,
    showDataSetPublishedOnly,speciesGroupMap,
    activeFilters,
    showGbifData, isSensitive, getSampleSize,
  }
})
